// Reports do aluno: bug reports + feature requests num único view.
// Aluno pode listar seus reports, ver resposta do admin e criar novos.
//
// Fluxo:
//   - Lista (cards) — kind + título + status + indicador de resposta nova
//   - Clicar abre detalhe inline (descrição, contexto auto, resposta do admin)
//   - Botão "Novo report" abre form modal com radio kind=bug|feature

import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";
import {
  AlertCircle,
  Bug,
  CheckCircle2,
  Clock,
  Hammer,
  LifeBuoy,
  Lightbulb,
  Loader2,
  MessageSquareText,
  Plus,
  RefreshCw,
  Star,
  ScrollText,
  X,
  XCircle
} from "lucide-react";
import {
  api,
  ApiError,
  type UserReportKind,
  type UserReportRow,
  type UserReportStatus
} from "./api";
import { linkify } from "./linkify";
import { openAlert } from "./ConfirmPromptModal";

interface ReportsState {
  phase: "idle" | "loading" | "ready" | "error";
  list: UserReportRow[];
  errMsg: string | null;
  lastLoadedAt: number;
}

export const reportsState = signal<ReportsState>({
  phase: "idle",
  list: [],
  errMsg: null,
  lastLoadedAt: 0
});

const composeOpen = signal<UserReportKind | null>(null);

export function openNewReport(kind: UserReportKind = "bug"): void {
  composeOpen.value = kind;
}

export async function loadReports(): Promise<void> {
  reportsState.value = { ...reportsState.value, phase: "loading", errMsg: null };
  try {
    const res = await api.listMyReports();
    reportsState.value = {
      phase: "ready",
      list: res.reports,
      errMsg: null,
      lastLoadedAt: Date.now()
    };
  } catch (err) {
    reportsState.value = {
      ...reportsState.value,
      phase: "error",
      errMsg: err instanceof Error ? err.message : String(err)
    };
  }
}

const STATUS_LABEL: Record<UserReportStatus, string> = {
  open: "Aberto",
  in_progress: "Em andamento",
  resolved: "Resolvido",
  wontfix: "Não será feito"
};

const STATUS_TONE: Record<UserReportStatus, string> = {
  open: "bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-500/30",
  in_progress:
    "bg-sky-500/10 text-sky-700 dark:text-sky-300 border-sky-500/30",
  resolved:
    "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/30",
  wontfix:
    "bg-muted/40 text-muted-foreground border-border"
};

export function MeusReportsView() {
  const state = reportsState.value;
  const composing = composeOpen.value;
  const [expanded, setExpanded] = useState<string | null>(null);

  useEffect(() => {
    if (state.phase === "idle") void loadReports();
  }, []);

  return (
    <div class="space-y-5">
      <header class="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 class="flex items-center gap-2 text-2xl font-bold tracking-tight">
            <LifeBuoy className="size-6 text-primary" />
            Ajuda / Reportar
          </h1>
          <p class="mt-1 text-sm text-muted-foreground">
            Dúvidas, bugs e sugestões que você enviou. A equipe responde aqui
            mesmo.
          </p>
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void loadReports()}
            class="inline-flex items-center gap-1.5 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold hover:bg-accent"
            title="Atualizar"
          >
            <RefreshCw width="1.1em" height="1.1em" />
            Atualizar
          </button>
          <button
            type="button"
            onClick={() => openNewReport("suporte")}
            class="inline-flex items-center gap-1.5 rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-500/20 dark:text-emerald-300"
          >
            <LifeBuoy width="1.1em" height="1.1em" />
            Suporte / Dúvida
          </button>
          <button
            type="button"
            onClick={() => openNewReport("bug")}
            class="inline-flex items-center gap-1.5 rounded-md border border-red-500/40 bg-red-500/10 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-500/20 dark:text-red-300"
          >
            <Bug width="1.1em" height="1.1em" />
            Reportar bug
          </button>
          <button
            type="button"
            onClick={() => openNewReport("feature")}
            class="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-3 py-1.5 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 dark:text-amber-300"
          >
            <Lightbulb width="1.1em" height="1.1em" />
            Sugerir feature
          </button>
          <button
            type="button"
            onClick={() => openNewReport("bank_request")}
            class="inline-flex items-center gap-1.5 rounded-md border border-sky-500/40 bg-sky-500/10 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-500/20 dark:text-sky-300"
          >
            <ScrollText width="1.1em" height="1.1em" />
            Pedir banco
          </button>
          <button
            type="button"
            onClick={() => openNewReport("feedback")}
            class="inline-flex items-center gap-1.5 rounded-md border border-violet-500/40 bg-violet-500/10 px-3 py-1.5 text-xs font-semibold text-violet-700 hover:bg-violet-500/20 dark:text-violet-300"
          >
            <Star width="1.1em" height="1.1em" />
            Deixar feedback
          </button>
        </div>
      </header>

      {state.phase === "loading" && (
        <div class="flex items-center justify-center gap-2 rounded-md border border-border bg-card/40 px-4 py-8 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          Carregando…
        </div>
      )}

      {state.phase === "error" && (
        <div class="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3">
          <div class="flex items-start gap-2">
            <AlertCircle className="mt-0.5 size-5 shrink-0 text-destructive" />
            <div class="space-y-1.5">
              <h3 class="text-sm font-bold">Não consegui carregar seus reports</h3>
              {state.errMsg && (
                <p class="text-xs text-muted-foreground">{state.errMsg}</p>
              )}
              <button
                type="button"
                onClick={() => void loadReports()}
                class="mt-1 rounded-md border border-border bg-background px-3 py-1 text-xs font-semibold hover:bg-accent"
              >
                Tentar de novo
              </button>
            </div>
          </div>
        </div>
      )}

      {state.phase === "ready" && state.list.length === 0 && (
        <EmptyState />
      )}

      {state.phase === "ready" && state.list.length > 0 && (
        <ul class="space-y-2">
          {state.list.map((r) => (
            <ReportCard
              key={r.id}
              report={r}
              expanded={expanded === r.id}
              onToggle={() => setExpanded(expanded === r.id ? null : r.id)}
            />
          ))}
        </ul>
      )}

      {composing && (
        <ComposeModal
          kind={composing}
          onClose={() => {
            composeOpen.value = null;
          }}
          onCreated={() => {
            composeOpen.value = null;
            void loadReports();
          }}
        />
      )}
    </div>
  );
}

function EmptyState() {
  return (
    <div class="rounded-md border border-dashed border-border bg-card/30 px-4 py-10 text-center">
      <Hammer className="mx-auto mb-2 size-6 text-muted-foreground" />
      <h3 class="text-sm font-bold">Você ainda não enviou nenhum report</h3>
      <p class="mt-1 text-xs text-muted-foreground">
        Encontrou um bug ou tem uma ideia? Os botões em cima abrem o formulário.
      </p>
    </div>
  );
}

function ReportCard({
  report,
  expanded,
  onToggle
}: {
  report: UserReportRow;
  expanded: boolean;
  onToggle: () => void;
}) {
  const kindIcon: Record<UserReportKind, typeof Bug> = {
    bug: Bug,
    feature: Lightbulb,
    bank_request: ScrollText,
    feedback: Star,
    suporte: LifeBuoy
  };
  const kindColor: Record<UserReportKind, string> = {
    bug: "text-red-600 dark:text-red-400",
    feature: "text-amber-600 dark:text-amber-400",
    bank_request: "text-sky-600 dark:text-sky-400",
    feedback: "text-violet-600 dark:text-violet-400",
    suporte: "text-emerald-600 dark:text-emerald-400"
  };
  const Icon = kindIcon[report.kind] ?? MessageSquareText;
  const iconColor = kindColor[report.kind] ?? "text-muted-foreground";
  const hasResponse = !!report.adminResponse;

  return (
    <li class="rounded-md border border-border bg-card/40">
      <button
        type="button"
        onClick={onToggle}
        class="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-accent/40"
        {...(expanded
          ? ({ "aria-expanded": "true" } as const)
          : ({ "aria-expanded": "false" } as const))}
      >
        <Icon className={`mt-0.5 size-5 shrink-0 ${iconColor}`} />
        <div class="min-w-0 flex-1">
          <div class="flex items-start justify-between gap-2">
            <h3 class="truncate text-sm font-bold">{report.title}</h3>
            <span
              class={`shrink-0 rounded-md border px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide ${STATUS_TONE[report.status]}`}
            >
              {STATUS_LABEL[report.status]}
            </span>
          </div>
          <div class="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-muted-foreground">
            <span class="inline-flex items-center gap-0.5">
              <Clock width="11" height="11" />
              {formatDate(report.createdAt)}
            </span>
            {report.kind === "feedback" && report.rating && (
              <span class="inline-flex items-center gap-0.5 font-semibold text-amber-600 dark:text-amber-400">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star
                    key={i}
                    width="11"
                    height="11"
                    className={
                      i < (report.rating ?? 0)
                        ? "fill-amber-400 text-amber-500"
                        : "text-muted-foreground/30"
                    }
                  />
                ))}
              </span>
            )}
            {hasResponse && (
              <span class="inline-flex items-center gap-0.5 rounded-md bg-emerald-500/10 px-1.5 py-0.5 font-semibold text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 width="11" height="11" />
                Resposta da equipe
              </span>
            )}
          </div>
        </div>
      </button>

      {expanded && (
        <div class="space-y-3 border-t border-border px-4 py-3 text-sm">
          {report.description && (
            <div>
              <div class="mb-1 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                Sua descrição
              </div>
              <p class="whitespace-pre-line text-sm leading-relaxed text-foreground">
                {report.description}
              </p>
            </div>
          )}
          {report.adminResponse && (
            <div class="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3">
              <div class="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
                <CheckCircle2 width="11" height="11" />
                Resposta {report.adminName ? `de ${report.adminName}` : "da equipe"}
                {report.respondedAt && (
                  <span class="font-normal normal-case opacity-80">
                    · {formatDate(report.respondedAt)}
                  </span>
                )}
              </div>
              <p class="whitespace-pre-line text-sm leading-relaxed text-foreground">
                {linkify(report.adminResponse)}
              </p>
            </div>
          )}
        </div>
      )}
    </li>
  );
}

interface KindMeta {
  label: string;
  icon: typeof Bug;
  color: string;
  titlePlaceholder: string;
  descPlaceholder: string;
  successMsg: string;
  needsRating?: boolean;
}

const KIND_META: Record<UserReportKind, KindMeta> = {
  suporte: {
    label: "suporte",
    icon: LifeBuoy,
    color: "text-emerald-600 dark:text-emerald-400",
    titlePlaceholder: "Ex: Não consigo abrir minha prova agendada",
    descPlaceholder:
      "Descreva sua dúvida ou o problema com o máximo de detalhes. A equipe responde aqui mesmo.",
    successMsg:
      "Recebemos sua solicitação de suporte. Você será notificado aqui quando respondermos."
  },
  bug: {
    label: "bug",
    icon: Bug,
    color: "text-red-600 dark:text-red-400",
    titlePlaceholder: "Ex: A modal de pagamento não fecha no celular",
    descPlaceholder:
      "Conta os passos pra reproduzir, o que esperava acontecer e o que aconteceu de fato.",
    successMsg:
      "Recebemos seu report. Você vai ser notificado aqui quando tivermos uma resposta."
  },
  feature: {
    label: "sugestão",
    icon: Lightbulb,
    color: "text-amber-600 dark:text-amber-400",
    titlePlaceholder: "Ex: Permitir exportar provas em PDF",
    descPlaceholder:
      "Conta como essa feature ajudaria você. Se conhece algum produto que faz isso bem, cita também!",
    successMsg: "Recebemos sua sugestão. Vamos avaliar e te dar um retorno!"
  },
  bank_request: {
    label: "pedido de banco",
    icon: ScrollText,
    color: "text-sky-600 dark:text-sky-400",
    titlePlaceholder: "Ex: ENEM 2023 — Linguagens e Códigos",
    descPlaceholder:
      "Especifica a prova/vestibular/concurso, o ano e qualquer outra informação que ajude a achar (link da prova, edital, etc).",
    successMsg:
      "Pedido recebido. Quando o banco estiver disponível, você é notificado aqui mesmo."
  },
  feedback: {
    label: "feedback",
    icon: Star,
    color: "text-violet-600 dark:text-violet-400",
    titlePlaceholder: "Ex: A experiência no celular tá ótima",
    descPlaceholder:
      "Conta o que está funcionando bem, o que ainda incomoda, e o que você acha que faltaria. Vai ajudar a moldar o que vamos priorizar.",
    successMsg:
      "Valeu pelo feedback! Toda nota e comentário ajuda a gente a melhorar.",
    needsRating: true
  }
};

function ComposeModal({
  kind,
  onClose,
  onCreated
}: {
  kind: UserReportKind;
  onClose: () => void;
  onCreated: () => void;
}) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [rating, setRating] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const meta = KIND_META[kind];
  const Icon = meta.icon;

  async function handleSubmit() {
    if (!title.trim()) {
      setErr("Título obrigatório.");
      return;
    }
    if (meta.needsRating && (rating < 1 || rating > 5)) {
      setErr("Escolha de 1 a 5 estrelas pra deixar feedback.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      const context = {
        url: typeof window !== "undefined" ? window.location.href : null,
        path: typeof window !== "undefined" ? window.location.pathname : null,
        userAgent: typeof navigator !== "undefined" ? navigator.userAgent : null,
        viewport:
          typeof window !== "undefined"
            ? { w: window.innerWidth, h: window.innerHeight }
            : null
      };
      await api.createReport({
        kind,
        title: title.trim(),
        description: description.trim() || undefined,
        rating: meta.needsRating ? rating : undefined,
        context
      });
      onCreated();
      void openAlert({ title: "Obrigado!", message: meta.successMsg });
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : e instanceof Error
            ? e.message
            : "Erro inesperado.";
      setErr(msg);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      class="fixed inset-0 z-[200] flex items-end justify-center bg-black/50 p-0 sm:items-center sm:p-4"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        class="flex w-full max-w-lg flex-col rounded-t-2xl bg-background shadow-2xl sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header class="flex items-center gap-3 border-b border-border px-4 py-3">
          <Icon className={`size-5 ${meta.color}`} />
          <h2 class="flex-1 text-base font-bold capitalize">{meta.label}</h2>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Fechar"
            class="rounded-md p-1.5 text-muted-foreground hover:bg-accent disabled:opacity-50"
          >
            <X className="size-4" />
          </button>
        </header>

        <div class="space-y-4 px-4 py-4">
          <div class="space-y-1.5">
            <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Título <span class="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={title}
              onInput={(e) => setTitle((e.target as HTMLInputElement).value)}
              maxLength={200}
              placeholder={meta.titlePlaceholder}
              disabled={submitting}
              autoFocus
              class="w-full rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <div class="text-right text-[10px] tabular-nums text-muted-foreground/70">
              {title.length}/200
            </div>
          </div>

          {meta.needsRating && (
            <div class="space-y-1.5">
              <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                Nota geral <span class="text-red-500">*</span>
              </label>
              <div class="flex items-center gap-1.5" role="radiogroup">
                {[1, 2, 3, 4, 5].map((n) => (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setRating(n)}
                    disabled={submitting}
                    aria-label={`${n} estrela${n > 1 ? "s" : ""}`}
                    {...(rating === n
                      ? ({ "aria-pressed": "true" } as const)
                      : ({ "aria-pressed": "false" } as const))}
                    class="rounded-md p-1.5 transition hover:scale-110 disabled:opacity-50"
                  >
                    <Star
                      width="2em"
                      height="2em"
                      className={
                        rating >= n
                          ? "fill-amber-400 text-amber-500"
                          : "text-muted-foreground/40"
                      }
                    />
                  </button>
                ))}
                {rating > 0 && (
                  <span class="ml-2 text-sm font-semibold text-muted-foreground">
                    {rating === 5
                      ? "Excelente!"
                      : rating === 4
                        ? "Bom"
                        : rating === 3
                          ? "Regular"
                          : rating === 2
                            ? "Ruim"
                            : "Péssimo"}
                  </span>
                )}
              </div>
            </div>
          )}

          <div class="space-y-1.5">
            <label class="text-xs font-bold uppercase tracking-wider text-muted-foreground">
              Detalhes
            </label>
            <textarea
              value={description}
              onInput={(e) =>
                setDescription((e.target as HTMLTextAreaElement).value)
              }
              maxLength={5000}
              rows={7}
              placeholder={meta.descPlaceholder}
              disabled={submitting}
              class="w-full resize-y rounded-md border border-border bg-background px-3 py-2 text-sm outline-none focus:border-primary disabled:opacity-60"
            />
            <div class="text-right text-[10px] tabular-nums text-muted-foreground/70">
              {description.length}/5000
            </div>
          </div>

          {err && (
            <div class="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2">
              <XCircle className="mt-0.5 size-4 shrink-0 text-destructive" />
              <p class="text-xs text-foreground">{err}</p>
            </div>
          )}

          <p class="text-[11px] text-muted-foreground">
            Vamos coletar automaticamente a URL e o navegador onde você está, pra
            ajudar na investigação.
          </p>
        </div>

        <footer class="flex items-center justify-end gap-2 border-t border-border bg-card/30 px-4 py-3">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            class="rounded-md border border-border bg-background px-3 py-1.5 text-sm font-semibold hover:bg-accent disabled:opacity-60"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={() => void handleSubmit()}
            disabled={submitting || !title.trim()}
            class="inline-flex items-center gap-1.5 rounded-md bg-primary px-4 py-1.5 text-sm font-bold text-white shadow-sm hover:brightness-110 disabled:opacity-60"
          >
            {submitting ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Plus width="1.1em" height="1.1em" />
            )}
            Enviar
          </button>
        </footer>
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yy = d.getFullYear();
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${dd}/${mm}/${yy} ${hh}:${mi}`;
}
